import React, { useState } from 'react';
import SectionHeader from '../SectionHeader';
import { FERTILITY_DEMO_SECTION_BORDER } from '../../designSystem';

const MOOD_OPTIONS = [
  { emoji: '😩', label: 'Rough' },
  { emoji: '😢', label: 'Sad' },
  { emoji: '😞', label: 'Low' },
  { emoji: '😐', label: 'Okay' },
  { emoji: '😑', label: 'Meh' },
  { emoji: '🙂', label: 'Good' },
  { emoji: '😊', label: 'Great' },
  { emoji: '😄', label: 'Happy' },
  { emoji: '🤩', label: 'Amazing' },
];

const SYMPTOM_OPTIONS = [
  'Cramps',
  'Headache',
  'Bloating',
  'Fatigue',
  'Nausea',
  'Hot flash',
  'Mood shift',
  'Tenderness',
  'Other',
];

const STEP_CIRCLE = {
  width: 26,
  height: 26,
  minWidth: 26,
  borderRadius: '50%',
  background: '#FBEAF0',
  color: '#D4537E',
  fontSize: 11,
  fontWeight: 700,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
};

/**
 * Today's log: mood, symptoms, and note. Self-contained, no required props.
 */
export default function MoodTracker({ fertilityDemoSectionBorder = false } = {}) {
  const [mood, setMood] = useState(null);
  const [symptoms, setSymptoms] = useState([]);
  const [note, setNote] = useState('');

  const toggleSymptom = (s) => {
    setSymptoms((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]));
  };

  const handleSave = () => {
    if (mood === null) return;
    const data = {
      mood,
      symptoms,
      note,
    };
    console.log(data);
    setMood(null);
    setSymptoms([]);
    setNote('');
  };

  return (
    <div
      style={{
        background: 'var(--card)',
        border: fertilityDemoSectionBorder ? FERTILITY_DEMO_SECTION_BORDER : '0.5px solid var(--border)',
        borderRadius: 14,
        padding: '16px 20px',
      }}
    >
      <div style={{ marginBottom: 10 }}>
        <SectionHeader
          title="Today's log"
          descriptor="How are you feeling and what happened today."
        />
      </div>
      <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 14 }}>
        {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
      </div>
      {/* Step 1: Mood */}
      <div style={{ display: 'flex', gap: 12 }}>
        <div style={STEP_CIRCLE}>1</div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 11, fontWeight: 700, marginBottom: 8, color: 'var(--text)' }}>
            How are you feeling?
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {MOOD_OPTIONS.map((opt, idx) => {
              const selected = mood === idx;
              return (
                <button
                  key={idx}
                  type="button"
                  onClick={() => setMood(idx)}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 4,
                    padding: 4,
                    borderRadius: 10,
                    border: `2px solid ${selected ? 'var(--pink)' : 'transparent'}`,
                    background: selected ? 'var(--pink-l)' : 'transparent',
                    cursor: 'pointer',
                  }}
                >
                  <span style={{ fontSize: 26, lineHeight: 1 }}>{opt.emoji}</span>
                  <span
                    style={{
                      fontSize: 8.5,
                      fontWeight: selected ? 700 : 400,
                      color: selected ? 'var(--pink)' : 'var(--muted)',
                    }}
                  >
                    {opt.label}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div style={{ borderTop: '1px solid var(--border)', margin: '14px 0' }} />

      {/* Step 2: Symptoms */}
      <div style={{ display: 'flex', gap: 12 }}>
        <div style={STEP_CIRCLE}>2</div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 11, fontWeight: 700, marginBottom: 8, color: 'var(--text)' }}>
            Symptoms <span style={{ fontWeight: 400, color: 'var(--muted)' }}>(optional)</span>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 4 }}>
            {SYMPTOM_OPTIONS.map((s) => {
              const selected = symptoms.includes(s);
              return (
                <button
                  key={s}
                  type="button"
                  onClick={() => toggleSymptom(s)}
                  style={{
                    fontSize: 11,
                    padding: '5px 11px',
                    borderRadius: 999,
                    border: `1.5px solid ${selected ? 'var(--pink)' : 'var(--border)'}`,
                    background: selected ? 'var(--pink-l)' : 'transparent',
                    color: selected ? 'var(--pink-dk)' : 'var(--muted)',
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  {s}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div style={{ borderTop: '1px solid var(--border)', margin: '14px 0' }} />

      {/* Step 3: Note */}
      <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
        <div style={STEP_CIRCLE}>3</div>
        <div style={{ flex: 1 }}>
          <input
            type="text"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Add a note for yourself (optional)..."
            style={{
              width: '100%',
              border: '1.5px solid var(--border)',
              borderRadius: 10,
              padding: '9px 12px',
              fontSize: 12,
              fontFamily: 'inherit',
              outline: 'none',
              color: 'var(--text)',
              boxSizing: 'border-box',
            }}
          />
        </div>
      </div>

      {/* Save button */}
      <button
        type="button"
        onClick={handleSave}
        disabled={mood === null}
        style={{
          marginTop: 16,
          width: '100%',
          padding: 11,
          fontSize: 13,
          fontWeight: 700,
          fontFamily: 'inherit',
          border: 'none',
          borderRadius: 10,
          background: mood !== null ? 'var(--pink)' : 'var(--sand-2)',
          color: mood !== null ? 'white' : 'var(--muted)',
          cursor: mood !== null ? 'pointer' : 'default',
        }}
      >
        Save today's log
      </button>
    </div>
  );
}
